import React, { useEffect, useState } from 'react';
import { queryAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

// ── Shared page wrapper with fade-in ──────────────────────────────────────

function PageWrapper({ children }) {
  const [in_, setIn] = useState(false);
  useEffect(() => { const t = setTimeout(() => setIn(true), 60); return () => clearTimeout(t); }, []);
  return (
    <div style={{
      padding: '40px 48px', maxWidth: 920,
      opacity: in_ ? 1 : 0,
      transform: in_ ? 'translateY(0)' : 'translateY(14px)',
      transition: 'opacity 0.4s ease, transform 0.4s ease'
    }}>
      {children}
    </div>
  );
}

function PageTitle({ title, subtitle }) {
  return (
    <div style={{ animation: 'fadeInUp 0.4s ease 0.1s both', marginBottom: 28 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1a1a1a', letterSpacing: '-0.3px' }}>{title}</h1>
      <p style={{ fontSize: 14, color: '#6b6b6b', marginTop: 6 }}>{subtitle}</p>
    </div>
  );
}

// ── Saved Queries ─────────────────────────────────────────────────────────

export function SavedQueries() {
  const [saved,   setSaved]   = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    queryAPI.saved()
      .then(res => setSaved(res.data.saved || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const runQuery = (q) => navigate('/', { state: { query: q } });

  return (
    <PageWrapper>
      <PageTitle title="Saved Queries" subtitle="Your bookmarked queries for quick access" />

      {loading ? (
        <div style={{ display: 'flex', gap: 8, padding: '48px 0', justifyContent: 'center' }}>
          {[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#1B5E3B', animation: `bounce 1.4s ease-in-out ${i*0.2}s infinite` }} />)}
        </div>
      ) : saved.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '64px 0', color: '#9b9b9b', fontSize: 14, animation: 'fadeIn 0.4s ease 0.2s both' }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
            <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
          </svg>
          No saved queries yet. Use the Save button after running a query.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {saved.map((item, i) => (
            <div
              key={i}
              className="card-hover"
              style={{
                background: '#fff', border: '1px solid #e8e6e0', borderRadius: 14,
                padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                animation: `fadeInUp 0.35s ease ${i * 60}ms both`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>{item.name}</div>
                  <div style={{ fontSize: 13, color: '#6b6b6b' }}>{item.query_text}</div>
                </div>
                <button
                  onClick={() => runQuery(item.query_text)}
                  className="btn-lift"
                  style={{
                    background: 'linear-gradient(135deg, #1B5E3B, #2E7D52)', color: '#fff',
                    border: 'none', borderRadius: 8, padding: '7px 16px',
                    fontSize: 12, cursor: 'pointer', fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(27,94,59,0.3)', whiteSpace: 'nowrap', flexShrink: 0
                  }}
                >
                  Run
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#9b9b9b' }}>
                {new Date(item.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            </div>
          ))}
        </div>
      )}
    </PageWrapper>
  );
}

// ── Schema Explorer ───────────────────────────────────────────────────────

export function SchemaExplorer() {
  const [schema,   setSchema]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    queryAPI.schema()
      .then(res => setSchema(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = (t) => setExpanded(p => ({ ...p, [t]: !p[t] }));

  return (
    <PageWrapper>
      <PageTitle title="Schema Explorer" subtitle="ERP database tables available for natural language queries" />

      {loading ? (
        <div style={{ display: 'flex', gap: 8, padding: '48px 0', justifyContent: 'center' }}>
          {[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#1B5E3B', animation: `bounce 1.4s ease-in-out ${i*0.2}s infinite` }} />)}
        </div>
      ) : schema && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {schema.tables.map((table, i) => (
            <div
              key={table}
              className="card-hover"
              style={{
                background: '#fff', border: '1px solid #e8e6e0',
                borderRadius: 12, overflow: 'hidden',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                animation: `fadeInUp 0.35s ease ${i * 50}ms both`
              }}
            >
              <button
                onClick={() => toggle(table)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 18px', background: 'none', border: 'none',
                  width: '100%', cursor: 'pointer', textAlign: 'left'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: expanded[table] ? 'linear-gradient(135deg, #1B5E3B, #2E7D52)' : '#E8F6EE',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background 0.2s ease', flexShrink: 0
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke={expanded[table] ? '#fff' : '#1B5E3B'} strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <line x1="3" y1="9" x2="21" y2="9"/>
                      <line x1="3" y1="15" x2="21" y2="15"/>
                      <line x1="9" y1="3" x2="9" y2="21"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a', fontFamily: 'JetBrains Mono, monospace' }}>{table}</div>
                    <div style={{ fontSize: 12, color: '#6b6b6b', marginTop: 1 }}>{schema.definitions[table]?.description}</div>
                  </div>
                </div>
                <div style={{
                  width: 22, height: 22, borderRadius: 6, background: '#E8F6EE',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transform: expanded[table] ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.25s ease'
                }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#1B5E3B" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
              </button>

              {expanded[table] && (
                <div style={{ borderTop: '1px solid #f0ede8', padding: '4px 0 8px' }}>
                  {Object.entries(schema.definitions[table]?.columns || {}).map(([col, desc], j) => (
                    <div
                      key={col}
                      style={{
                        display: 'flex', gap: 16, padding: '7px 18px',
                        alignItems: 'baseline',
                        animation: `fadeIn 0.2s ease ${j * 20}ms both`
                      }}
                    >
                      <span style={{
                        fontSize: 12, fontWeight: 600, color: '#1B5E3B',
                        fontFamily: 'JetBrains Mono, monospace', minWidth: 160, flexShrink: 0
                      }}>{col}</span>
                      <span style={{ fontSize: 12, color: '#9b9b9b' }}>{desc}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </PageWrapper>
  );
}

// ── Settings ──────────────────────────────────────────────────────────────

export function Settings() {
  const settings = [
    { label: 'AI Model',             value: 'GPT-4o',        icon: '🤖' },
    { label: 'Max retries',          value: '2',              icon: '🔁' },
    { label: 'Max rows returned',    value: '1,000',          icon: '📊' },
    { label: 'Query timeout',        value: '60 seconds',     icon: '⏱️' },
    { label: 'Rate limit',           value: '100 / 15 min',   icon: '🚦' },
    { label: 'Export formats',       value: 'PDF, Excel',     icon: '📄' },
    { label: 'Auth',                 value: 'JWT / 24h TTL',  icon: '🔐' },
    { label: 'Pipeline layers',      value: '5 (L1–L5)',      icon: '⚙️' },
  ];

  return (
    <PageWrapper>
      <PageTitle title="Settings" subtitle="Current system configuration — read only" />

      <div style={{
        background: '#fff', border: '1px solid #e8e6e0', borderRadius: 14,
        overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        animation: 'fadeInUp 0.4s ease 0.2s both'
      }}>
        {settings.map((s, i) => (
          <div
            key={s.label}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 20px',
              borderBottom: i < settings.length - 1 ? '1px solid #f0ede8' : 'none',
              animation: `fadeIn 0.3s ease ${i * 50}ms both`
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 16 }}>{s.icon}</span>
              <span style={{ fontSize: 13, color: '#4b4b4b', fontWeight: 500 }}>{s.label}</span>
            </div>
            <span style={{
              fontSize: 13, fontWeight: 700, color: '#1B5E3B',
              background: '#E8F6EE', padding: '3px 10px', borderRadius: 8,
              border: '1px solid #C8EDD6'
            }}>{s.value}</span>
          </div>
        ))}
      </div>

      {/* Pipeline diagram */}
      <div style={{ marginTop: 24, animation: 'fadeInUp 0.4s ease 0.5s both' }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14, color: '#1a1a1a' }}>AI Pipeline</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            { l: 'L1', name: 'Intent Classifier',  desc: 'Safety check + intent scoring',           color: '#1B5E3B' },
            { l: 'L2', name: 'Semantic Layer',      desc: 'Business terms → ERP schema mapping',     color: '#8b5cf6' },
            { l: 'L3', name: 'Schema Injector',     desc: 'Focused schema context for GPT-4o',       color: '#4CAF7D' },
            { l: 'L4', name: 'SQL Generator',       desc: 'GPT-4o → validated SQL with retry loop',  color: '#81C784' },
            { l: 'L5', name: 'Result Interpreter',  desc: 'Plain-English summary + confidence',      color: '#C8EDD6' },
          ].map((layer, i) => (
            <div
              key={layer.l}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                background: '#fff', border: '1px solid #e8e6e0', borderRadius: 10,
                padding: '12px 16px',
                animation: `fadeInLeft 0.35s ease ${0.5 + i * 0.07}s both`
              }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: 8, background: layer.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 800, color: i > 2 ? '#4c1d95' : '#fff',
                flexShrink: 0
              }}>{layer.l}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{layer.name}</div>
                <div style={{ fontSize: 12, color: '#9b9b9b', marginTop: 1 }}>{layer.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PageWrapper>
  );
}
